import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Heart, User, Menu, X, ChevronDown } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV_ORDER = ["Bobble Hats", "Beanies", "Winter Bucket Hats", "Headbands", "Scarves"];
const FALLBACK_NAV = [
  { name: "Bobble Hats", slug: "crochet-beanies-soft-and-cosy-crochet-kerry", subcategories: [] },
  { name: "Beanies", slug: "beanies", subcategories: [] },
  { name: "Winter Bucket Hats", slug: "winter-bucket-hats", subcategories: [] },
  { name: "Headbands", slug: "earwarmers-dont-like-a-hat-choose-our-cosy-ear-warmers-crochet-kerry", subcategories: [] },
  { name: "Scarves", slug: "crochet-scarves-chunky-and-cosy-crochet-kerry", subcategories: [] },
];

export default function Header() {
  const { cartCount, wishlist, user } = useStore();
  const [announce, setAnnounce] = useState("");
  const [nav, setNav] = useState(FALLBACK_NAV);
  const [openMenu, setOpenMenu] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/content/homepage").then((r) => setAnnounce(r.data?.data?.announcement_bar || "")).catch(() => {});
    api.get("/shopify/collections").then((r) => {
      const cols = r.data || [];
      const ordered = NAV_ORDER.map((name) => cols.find((c) => c.name.trim().toLowerCase() === name.toLowerCase())).filter(Boolean);
      if (ordered.length) setNav(ordered);
    }).catch(() => {});
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/shop?search=${encodeURIComponent(q.trim())}`); setSearchOpen(false); setQ(""); }
  };

  return (
    <header className="sticky top-0 z-50">
      {announce && (
        <div className="bg-brand-ink text-white text-xs tracking-luxe uppercase py-2 ck-marquee" data-testid="announcement-bar">
          <span>{announce} &nbsp;•&nbsp; {announce}</span>
        </div>
      )}
      <div className="bg-white/85 backdrop-blur-md border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24 sm:h-28">
            {/* mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2" data-testid="mobile-menu-button" aria-label="Open menu"><Menu className="w-5 h-5" /></button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 overflow-y-auto">
                <div className="font-serif-display text-2xl mb-6 mt-2">Crochet Kerry</div>
                <nav className="space-y-5">
                  {nav.map((c) => (
                    <div key={c.slug}>
                      <Link to={`/shop?collection=${c.slug}`} onClick={() => setMobileOpen(false)} className="block text-sm uppercase tracking-luxe mb-2">{c.name}</Link>
                      <div className="pl-3 space-y-2">
                        {(c.subcategories || []).map((s) => (
                          <Link key={s} to={`/shop?collection=${c.slug}&sub=${encodeURIComponent(s)}`} onClick={() => setMobileOpen(false)} className="block text-sm text-muted-foreground">{s}</Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>

            <Link to="/" data-testid="logo" aria-label="Crochet Kerry home" className="h-full flex items-center py-1.5">
              <img src="/logo-crochet-kerry.png" alt="Crochet Kerry" className="h-full w-auto object-contain" />
            </Link>

            {/* desktop nav */}
            <nav className="hidden lg:flex items-center gap-8" onMouseLeave={() => setOpenMenu(null)}>
              {nav.map((c) => (
                <div key={c.slug} className="relative" onMouseEnter={() => setOpenMenu(c.slug)}>
                  {(c.subcategories || []).length === 0 ? (
                    <Link to={`/shop?collection=${c.slug}`} className="text-sm uppercase tracking-luxe hover:text-brand-pink transition-colors" data-testid={`mega-menu-${c.slug}`}>{c.name}</Link>
                  ) : (
                    <button className="flex items-center gap-1 text-sm uppercase tracking-luxe hover:text-brand-pink transition-colors" data-testid={`mega-menu-${c.slug}`}>
                      {c.name} <ChevronDown className="w-3 h-3" />
                    </button>
                  )}
                  {openMenu === c.slug && (c.subcategories || []).length > 0 && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4">
                      <div className="bg-white border border-brand-border shadow-xl rounded-sm p-5 w-64 ck-fade-up" data-testid={`dropdown-${c.slug}`}>
                        <Link to={`/shop?collection=${c.slug}`} onClick={() => setOpenMenu(null)}
                          className="block pb-2 mb-1 text-sm uppercase tracking-luxe border-b border-brand-border hover:text-brand-pink transition-colors">View all {c.name}</Link>
                        {(c.subcategories || []).map((s) => (
                          <Link key={s} to={`/shop?collection=${c.slug}&sub=${encodeURIComponent(s)}`}
                            onClick={() => setOpenMenu(null)}
                            className="block py-2 text-sm text-muted-foreground hover:text-brand-ink hover:pl-1 transition-all">{s}</Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            <div className="flex items-center gap-3 sm:gap-4">
              <button onClick={() => setSearchOpen((v) => !v)} className="p-1 hover:text-brand-pink transition-colors" data-testid="search-toggle" aria-label="Search"><Search className="w-5 h-5" /></button>
              <Link to="/account?tab=wishlist" className="relative p-1 hover:text-brand-pink transition-colors" data-testid="wishlist-link" aria-label="Wishlist">
                <Heart className="w-5 h-5" />
                {wishlist.length > 0 && <span className="absolute -top-1 -right-1 bg-brand-pink text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{wishlist.length}</span>}
              </Link>
              <Link to="/account" className="p-1 hover:text-brand-pink transition-colors" data-testid="account-link" aria-label="Account"><User className="w-5 h-5" /></Link>
              <Link to="/cart" className="relative p-1 hover:text-brand-pink transition-colors" data-testid="cart-link" aria-label="Basket">
                <ShoppingBag className="w-5 h-5" />
                {cartCount > 0 && <span className="absolute -top-1 -right-1 bg-brand-ink text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center" data-testid="cart-count">{cartCount}</span>}
              </Link>
            </div>
          </div>
          {searchOpen && (
            <form onSubmit={doSearch} className="pb-4 ck-fade-up">
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search for hats, bags, scarves…"
                className="w-full border-b border-brand-border bg-transparent py-3 outline-none focus:border-brand-pink transition-colors" data-testid="search-input" />
            </form>
          )}
        </div>
      </div>
    </header>
  );
}
